import { type FragmentType, useFragment } from '@/gql/codegen';

import { graphql } from '@/gql/codegen/gql';
import { LessonTitle } from '@/routes/_embaUserLayout/-components/LessonTitle';
import { useLeaveBlocker } from '@/routes/_embaUserLayout/-hooks/useLeaveBlocker';
import type React from 'react';
import { useEffect, useState } from 'react';

import nextButton from '@/assets/images/next.svg';
import nextButtonDisabled from '@/assets/images/next_disabled.svg';
import prevButton from '@/assets/images/prev.svg';
import prevButtonDisabled from '@/assets/images/prev_disabled.svg';
import { SelfSummarizingQuestion } from './-components/SelfSummarizingQuestion';
import styles from './index.module.scss';

export const COURSE_LESSON_SELF_SUMMARIZING_FRAGMENT = graphql(/* GraphQL */ `
  fragment EmbaCourseLessonSelfSummarizing on CourseLessonSelfSummarizing {
    id
    questions {
      id
      ordinal
      content
      userAnswer {
        id
        content
      }
      ...EmbaCourseLessonSelfSummarizingQuestion
    }
  }
`);

export type CourseLessonSelfSummarizingPageProps = {
  content: FragmentType<typeof COURSE_LESSON_SELF_SUMMARIZING_FRAGMENT>;
  lessonName: string;
  chapterName: string;
  onEmbaCompleteCourseLesson: () => Promise<void>;
  answerSelfSummarizingQuestion: (
    content: string,
    embaSelfSummarizingQuestionId: number,
  ) => Promise<void>;
  answerSelfSummarizingQuestionLoading: boolean;
  isAnsweredModalOpen: boolean;
  closeAnsweredModal: () => void;
};

export const CourseLessonSelfSummarizingPageComponent = ({
  content,
  lessonName,
  chapterName,
  onEmbaCompleteCourseLesson,
  answerSelfSummarizingQuestion,
  answerSelfSummarizingQuestionLoading,
  isAnsweredModalOpen,
  closeAnsweredModal,
}: CourseLessonSelfSummarizingPageProps) => {
  const embaCourseLessonSelfSummarizingData = useFragment(
    COURSE_LESSON_SELF_SUMMARIZING_FRAGMENT,
    content,
  );

  const { questions } = embaCourseLessonSelfSummarizingData;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const currentQuestion = questions[currentQuestionIndex];
  const [answer, setAnswer] = useState('');
  const isAlreadyAnswered = currentQuestion.userAnswer !== null; // 回答済みかどうかの状態
  const allAnswered = questions.every(
    (question) => question.userAnswer !== null,
  );

  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  const { onBlocking, unBlocking, initBlocking } = useLeaveBlocker();
  useEffect(() => {
    allAnswered ? unBlocking() : onBlocking();
  }, [allAnswered, unBlocking, onBlocking]);
  initBlocking();

  const handleAnswerSelfSummarizingQuestion = async () => {
    // hooks側でerrorをthrowしているので、ここでのエラーハンドリングは不要
    await answerSelfSummarizingQuestion(answer, Number(currentQuestion.id));
  };

  const handleNext = () => {
    unBlocking();
    closeAnsweredModal();
    if (isLastQuestion && allAnswered) {
      onEmbaCompleteCourseLesson();
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setAnswer('');
      window.scrollTo({
        top: 0,
      });
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  return (
    <div>
      <LessonTitle lessonName={lessonName} chapterName={chapterName} />
      <div className={styles.selfSummarizingWrapper}>
        <div className={styles.questionArea}>
          <div className={styles.questionContentArea}>
            <div className={styles.questionHeader}>
              <div className={styles.questionLabel}>問</div>

              <div className={styles.questionHeaderRight}>
                <div className={styles.questionCount}>
                  {currentQuestionIndex + 1}/{questions.length}
                </div>
                <div className={styles.questionControll}>
                  {currentQuestionIndex === 0 ? (
                    <img src={prevButtonDisabled} alt="" />
                  ) : (
                    <button onClick={handlePrev} type="button">
                      <img src={prevButton} alt="" />
                    </button>
                  )}
                  {isLastQuestion || !isAlreadyAnswered ? (
                    <img src={nextButtonDisabled} alt="" />
                  ) : (
                    <button onClick={handleNext} type="button">
                      <img src={nextButton} alt="" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            <p className={styles.questionContent}>{currentQuestion.content}</p>
            <SelfSummarizingQuestion
              question={currentQuestion}
              answer={answer}
              setAnswer={setAnswer}
              isAlreadyAnswered={isAlreadyAnswered}
              handleAnswerSelfSummarizingQuestion={
                handleAnswerSelfSummarizingQuestion
              }
              handleNext={handleNext}
              isLastQuestion={isLastQuestion}
              allAnswered={allAnswered}
              isAnsweredModalOpen={isAnsweredModalOpen}
              closeAnsweredModal={closeAnsweredModal}
              answerSelfSummarizingQuestionLoading={
                answerSelfSummarizingQuestionLoading
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
